import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { AuthService } from '../../services/auth.service';
import { CustomerNotificationService } from '../../services/customer-notification.service';
import type {
  CustomerNotificationCategory,
  CustomerNotificationMode,
  CustomerNotificationTypeDTO,
  UpdateCustomerNotificationConfigRequest
} from '../../core/models';

interface ModeOption {
  value: CustomerNotificationMode;
  label: string;
  description: string;
}

interface CategoryGroup {
  category: CustomerNotificationCategory;
  label: string;
  types: CustomerNotificationTypeDTO[];
}

const CATEGORY_LABELS: Record<CustomerNotificationCategory, string> = {
  COLLECTION: 'Cobranza',
  SUBSCRIPTION: 'Suscripciones',
  PAYMENT: 'Pagos',
  INVOICE: 'Facturación',
  COMMUNICATION: 'Comunicaciones',
  MIT: 'Mandatos'
};

/**
 * Correos automáticos al cliente final de la compañía de la sesión. Cualquiera con acceso a la configuración la ve;
 * solo un administrador de Pento (`CUSTOMER_NOTIFICATION_CONFIG_WRITE`) la cambia. Se guarda con el botón general
 * de la página (ver {@link buildRequest}).
 */
@Component({
  selector: 'app-company-customer-notification-config',
  templateUrl: './company-customer-notification-config.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, FormsModule, ToggleSwitchModule]
})
export class CompanyCustomerNotificationConfigComponent {
  private auth = inject(AuthService);
  private service = inject(CustomerNotificationService);
  private companyId = this.auth.getCompanyId();

  readonly canEdit = this.auth.hasRole('CUSTOMER_NOTIFICATION_CONFIG_WRITE');
  readonly modes: ModeOption[] = [
    { value: 'STANDARD', label: 'Estándar', description: 'Pento envía todos los correos al cliente.' },
    {
      value: 'HEADLESS_CUSTOM',
      label: 'Headless personalizado',
      description: 'Solo se envían los correos automáticos que selecciones.'
    },
    {
      value: 'HEADLESS_FULL',
      label: 'Headless total',
      description: 'No se envía ningún correo automático configurable, incluidos los que se agreguen en el futuro.'
    }
  ];

  loading = signal(true);
  loadError = signal<string | null>(null);
  types = signal<CustomerNotificationTypeDTO[]>([]);
  mode = signal<CustomerNotificationMode>('STANDARD');
  /** Selección de `HEADLESS_CUSTOM`; se conserva al cambiar de modo y se envía siempre. */
  selected = signal<Set<string>>(new Set());

  /** Solo los tipos configurables; los manuales y los de mandatos siempre se envían y no se listan. */
  groups = computed<CategoryGroup[]>(() => {
    const groups: CategoryGroup[] = [];
    const configurable = this.types().filter((type) => type.configurable);
    for (const type of configurable.sort((a, b) => a.order - b.order)) {
      let group = groups.find((g) => g.category === type.category);
      if (!group) {
        group = { category: type.category, label: CATEGORY_LABELS[type.category] ?? type.category, types: [] };
        groups.push(group);
      }
      group.types.push(type);
    }
    return groups;
  });

  constructor() {
    this.load();
  }

  isSent(type: CustomerNotificationTypeDTO): boolean {
    switch (this.mode()) {
      case 'STANDARD':
        return true;
      case 'HEADLESS_FULL':
        return false;
      default:
        return this.selected().has(type.name);
    }
  }

  isLocked(): boolean {
    return !this.canEdit || this.mode() !== 'HEADLESS_CUSTOM';
  }

  selectMode(mode: CustomerNotificationMode): void {
    if (!this.canEdit) return;
    this.mode.set(mode);
  }

  toggle(type: CustomerNotificationTypeDTO, sent: boolean): void {
    if (this.isLocked()) return;
    const next = new Set(this.selected());
    if (sent) {
      next.add(type.name);
    } else {
      next.delete(type.name);
    }
    this.selected.set(next);
  }

  /** Sección para el guardado general de la página; nulo si el usuario no puede editarla o no cargó. */
  buildRequest(): UpdateCustomerNotificationConfigRequest | null {
    if (!this.canEdit || this.loading() || this.loadError()) return null;
    return { mode: this.mode(), enabled: [...this.selected()] };
  }

  private load(): void {
    if (this.companyId == null) {
      this.loading.set(false);
      this.loadError.set('No se pudo identificar la compañía de la sesión.');
      return;
    }
    forkJoin({ types: this.service.getTypes(), config: this.service.getConfig(this.companyId) }).subscribe({
      next: ({ types, config }) => {
        this.types.set(types.body ?? []);
        this.mode.set(config.body?.mode ?? 'STANDARD');
        this.selected.set(new Set(config.body?.enabled ?? []));
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.loadError.set('No se pudo cargar la configuración de notificaciones a clientes.');
      }
    });
  }
}
